/**
 * Fast-drive run_code observation helpers.
 *
 * @module
 */

import type {
  BrowserConsoleErrorSummary,
  BrowserDomSignals,
  IBrowserClient,
} from '../browser-client.interface.js';
import { diffReflessSnapshots } from '../refless-diff.js';

export type RunCodeBeforeObservation = {
  beforeUrl?: string;
  beforeSignals?: BrowserDomSignals;
  beforeReqCount: number;
  beforePageUrls: string[];
  beforeYaml: string;
};

export type RunCodePostActionSignals = {
  afterUrl?: string;
  afterSignals?: BrowserDomSignals;
  reqDelta: number;
  extra: string[];
};

export type SettleObservationText = {
  text: string;
  captureEmpty: boolean;
  afterUrl?: string;
};

export function formatRunCodeResult(result: unknown): string {
  if (result === undefined || result === null) return 'Code executed (no return value).';
  return typeof result === 'string' ? result : JSON.stringify(result, null, 2);
}

/**
 * One-evaluate read of in-page fast-drive signals. Falls back to the older
 * revision-only accessor for clients that do not expose dialog stamps.
 */
export async function readRunCodeDomSignals(
  client: IBrowserClient,
  signal?: AbortSignal
): Promise<BrowserDomSignals | undefined> {
  if (client.getDomSignals) {
    return await client.getDomSignals({ signal });
  }
  if (client.getDomRevision) {
    const rev = await client.getDomRevision({ signal });
    return { rev, dialogOpenTs: 0, dialogName: '' };
  }
  return undefined;
}

export async function captureRunCodeBeforeObservation(args: {
  client: IBrowserClient;
  signal?: AbortSignal;
  settle: boolean;
}): Promise<RunCodeBeforeObservation> {
  const { client, signal, settle } = args;
  return {
    beforeUrl: await client.getCurrentUrl({ signal }).catch(() => undefined),
    beforeSignals: await readRunCodeDomSignals(client, signal),
    beforeReqCount: client.getNetworkRequestCount?.() ?? 0,
    beforePageUrls: client.getOpenPageUrls?.() ?? [],
    beforeYaml: settle ? (client.getReflessYaml?.() ?? '') : '',
  };
}

/**
 * Read post-action signals shared by immediate and settle observation modes.
 * Extra lines are emitted only when they add information that is not otherwise
 * visible in the result/diff.
 */
export async function collectRunCodePostActionSignals(args: {
  client: IBrowserClient;
  signal?: AbortSignal;
  startedAtMs: number;
  beforeSignals?: BrowserDomSignals;
  beforeReqCount: number;
  beforePageUrls: string[];
}): Promise<RunCodePostActionSignals> {
  const { client, signal, startedAtMs, beforeSignals, beforeReqCount, beforePageUrls } = args;
  const afterUrl = await client.getCurrentUrl({ signal }).catch(() => undefined);
  const afterSignals = await readRunCodeDomSignals(client, signal);
  const reqDelta = Math.max(
    0,
    (client.getNetworkRequestCount?.() ?? beforeReqCount) - beforeReqCount
  );
  const extra: string[] = [];

  // URL-based detection (handles the net-zero close+open case). When the client
  // exposes stable page handles, annotate the new tab with its durable within-run
  // handle so the agent has an actionable reference to pass back to browser_tabs.
  const afterPageUrls = client.getOpenPageUrls?.() ?? [];
  const beforeSet = new Set(beforePageUrls);
  const newUrls = afterPageUrls.filter((u) => !beforeSet.has(u));
  if (newUrls.length > 0) {
    const newUrl = newUrls[0] || '';
    const handleMatch = client.getOpenPageHandles?.()?.find((p) => p.url === newUrl);
    const handleTag = handleMatch ? ` [handle=${handleMatch.handle}]` : '';
    extra.push(`Opened new tab${handleTag} → ${newUrl || '(blank)'}`);
  }

  if (
    beforeSignals &&
    afterSignals &&
    afterSignals.dialogOpenTs > 0 &&
    afterSignals.dialogOpenTs !== beforeSignals.dialogOpenTs
  ) {
    extra.push(`Modal opened${afterSignals.dialogName ? `: ${afterSignals.dialogName}` : ''}`);
  }

  const consoleErrors = client.getConsoleErrorSummarySince?.(startedAtMs);
  const consoleErrorLine = formatConsoleErrorLine(consoleErrors);
  if (consoleErrorLine) extra.push(consoleErrorLine);

  return { afterUrl, afterSignals, reqDelta, extra };
}

export function formatImmediateRunCodeObservation(args: {
  resultText: string;
  beforeUrl?: string;
  beforeSignals?: BrowserDomSignals;
  post: RunCodePostActionSignals;
  pendingNetworkCount: number;
}): string {
  const { resultText, beforeUrl, beforeSignals, post, pendingNetworkCount } = args;
  const beforeRev = beforeSignals?.rev ?? 0;
  const afterRev = post.afterSignals?.rev ?? beforeRev;
  const mutated = afterRev > beforeRev;
  const signals =
    `domMutated=${mutated} mutationDelta=${Math.max(0, afterRev - beforeRev)} ` +
    `networkRequestsTriggered=${post.reqDelta} pendingNetwork=${pendingNetworkCount}`;
  const hint =
    mutated || pendingNetworkCount > 0
      ? ' — something changed/loading; use observe:settle or browser_snapshot (refless) to see the result.'
      : ' — no change detected.';
  const navigated = !!post.afterUrl && !!beforeUrl && post.afterUrl !== beforeUrl;
  const urlLine = navigated
    ? `URL: ${beforeUrl} → ${post.afterUrl} (navigated)`
    : `URL: ${post.afterUrl ?? beforeUrl ?? '(unknown)'}`;
  const head = [urlLine, ...post.extra].join('\n');
  return `${resultText}\n${head}\n[observe:immediate] ${signals}${hint}`;
}

export function formatSettleRunCodeObservation(args: {
  resultText: string;
  beforeYaml: string;
  afterYaml: string;
  beforeUrl?: string;
  post: RunCodePostActionSignals;
}): SettleObservationText {
  const { resultText, beforeYaml, afterYaml, beforeUrl, post } = args;
  const extra =
    post.reqDelta > 0 ? [...post.extra, `networkRequestsTriggered=${post.reqDelta}`] : post.extra;
  const extraTail = extra.length ? `\n${extra.join('\n')}` : '';

  if (!afterYaml) {
    return {
      text: `${resultText}\n[observe:settle] could not capture page state — call browser_snapshot to perceive.${extraTail}`,
      captureEmpty: true,
      afterUrl: post.afterUrl,
    };
  }

  if (!beforeYaml) {
    const navNote =
      post.afterUrl && beforeUrl && post.afterUrl !== beforeUrl
        ? ` (navigated → ${post.afterUrl})`
        : '';
    return {
      text: `${resultText}\n[observe:settle] captured initial page state${navNote} — call browser_snapshot for the full tree.${extraTail}`,
      captureEmpty: false,
      afterUrl: post.afterUrl,
    };
  }

  const delta = diffReflessSnapshots(beforeYaml, afterYaml, {
    beforeUrl,
    afterUrl: post.afterUrl,
  });
  return {
    text: `${resultText}\n[observe:settle] ${delta}${extraTail}`,
    captureEmpty: false,
    afterUrl: post.afterUrl,
  };
}

function formatConsoleErrorLine(
  consoleErrors: BrowserConsoleErrorSummary | undefined
): string | undefined {
  if (!consoleErrors || consoleErrors.count === 0) return undefined;
  const first = consoleErrors.first
    ? `: ${consoleErrors.first.replace(/\s+/g, ' ').slice(0, 160)}`
    : '';
  return `Console errors (${consoleErrors.count})${first}`;
}
